import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import DashboardHero from './components/DashboardHero';
import KpiGrid from './components/KpiGrid';
import FallTimelineCard from './components/FallTimelineCard';
import LiveMonitoringSection from './components/LiveMonitoringSection';
import RecentEventsPanel from './components/RecentEventsPanel';
import { useDashboardData } from './hooks/useDashboardData';

const DashboardPage = () => {
  const {
    loading,
    error,
    stats,
    fallEvents,
    latestEvents,
  } = useDashboardData();
  const navigate = useNavigate();

  const unresolvedAlerts = useMemo(
    () => fallEvents.filter((event) => {
      const status = event.status?.toUpperCase?.() || '';
      return status && !['CLOSED', 'ADMIN_REVIEWED'].includes(status);
    }),
    [fallEvents],
  );

  const escalatedCount = useMemo(
    () => unresolvedAlerts.filter((event) => event.status?.toUpperCase?.() === 'ESCALATED').length,
    [unresolvedAlerts],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error || 'No dashboard data available.'}</Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 6,
        background: '#F6F7FB',
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, md: 5 },
          maxWidth: { lg: '1380px', xl: '1580px' },
          mx: 'auto',
        }}
      >
        <Stack spacing={3}>
          <DashboardHero stats={stats} navigate={navigate} />
          <KpiGrid stats={stats} navigate={navigate} />

          {unresolvedAlerts.length > 0 && (
            <Card
              sx={{
                borderRadius: '12px !important',
                p: { xs: 2.5, md: 3 },
                background: 'linear-gradient(135deg, rgba(248,113,113,0.08), rgba(248,113,113,0.02))',
                border: '1px solid rgba(248,113,113,0.18)',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(248,113,113,0.16)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'error.main',
                    }}
                  >
                    <ReportProblemIcon />
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {`Action needed: ${unresolvedAlerts.length} alert${unresolvedAlerts.length > 1 ? 's' : ''} awaiting review`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {escalatedCount > 0
                        ? `${escalatedCount} escalated incident${escalatedCount > 1 ? 's are' : ' is'} pending immediate attention.`
                        : 'Review these incidents to keep response times within target.'}
                    </Typography>
                  </Stack>
                </Stack>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => navigate('/fall-events')}
                >
                  Go to fall events
                </Button>
              </Stack>
            </Card>
          )}

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(0, 2fr) minmax(320px, 1fr)',
              },
              alignItems: 'stretch',
            }}
          >
            <FallTimelineCard events={fallEvents} />
            <RecentEventsPanel events={latestEvents} navigate={navigate} />
          </Box>

          <Box sx={{ mt: -1 }}>
            <LiveMonitoringSection stats={stats} events={latestEvents} navigate={navigate} />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default DashboardPage;
